import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrl, buildAuthHeadersForFetch } from "./RestService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import {
    IQboAttachmentsListResponse,
    IQboUploadAttachmentResponse,
    IQboDeleteAttachmentResponse,
    IQboUpdateAttachmentResponse,
} from "../models/DTO/IQboAttachmentDTO";
import axios, { AxiosProgressEvent, AxiosRequestConfig } from "axios";

/**
 * Response from GET /qbo/invoices/:qboEntityId/attachments/:attachmentGuid/download-url
 */
interface IQboDownloadUrlResponse {
    downloadUrl: string;
}

/**
 * Get list of attachments for a QBO invoice
 */
const getAttachments = async (
    qboEntityId: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IQboAttachmentsListResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.QBOInvoiceAttachments,
        params: { qboEntityId },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch attachments: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Get download URL for an attachment
 */
const getDownloadUrl = async (
    qboEntityId: string,
    attachmentGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IQboDownloadUrlResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.QBOInvoiceAttachmentDownloadUrl,
        params: { qboEntityId, attachmentGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to get download URL: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Upload attachment directly to QBO
 * Uses multipart form-data - the backend handles uploading to QBO
 */
const uploadAttachment = async (
    qboEntityId: string,
    file: File,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    options?: {
        accountingId?: number;
        includeOnSend?: boolean;
        note?: string;
        onProgress?: (percent: number) => void;
    }
): Promise<IQboUploadAttachmentResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.QBOInvoiceAttachments,
        params: { qboEntityId },
    });

    // Build FormData for multipart upload
    const formData = new FormData();
    formData.append("file", file);

    if (options?.accountingId !== undefined) {
        formData.append("accountingId", options.accountingId.toString());
    }
    if (options?.includeOnSend !== undefined) {
        formData.append("includeOnSend", options.includeOnSend.toString());
    }
    if (options?.note) {
        formData.append("note", options.note);
    }

    // Use axios for upload progress tracking
    const config: AxiosRequestConfig = {
        headers: {
            userToken: userModel.token,
            userGuid: userModel.guid,
            supplierGuid: supplierModel.guid,
            // Note: Don't set Content-Type for FormData - axios will set it with boundary
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (progressEvent.lengthComputable && options?.onProgress) {
                const percent = Math.round((progressEvent.loaded / (progressEvent.total || 1)) * 100);
                options.onProgress(percent);
            }
        },
    };

    const response = await axios.post(url, formData, config);

    if (response.status !== 201 && response.status !== 200) {
        throw new Error(response.data?.message || `Failed to upload attachment: ${response.statusText}`);
    }

    return response.data;
};

/**
 * Delete attachment from QBO
 */
const deleteAttachment = async (
    qboEntityId: string,
    attachmentGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IQboDeleteAttachmentResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.QBOInvoiceAttachment,
        params: { qboEntityId, attachmentGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "DELETE",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to delete attachment: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Update attachment settings (e.g., includeOnSend)
 */
const updateAttachment = async (
    qboEntityId: string,
    attachmentGuid: string,
    data: { includeOnSend: boolean },
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IQboUpdateAttachmentResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.QBOInvoiceAttachment,
        params: { qboEntityId, attachmentGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "PATCH",
        headers,
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw new Error(`Failed to update attachment: ${response.statusText}`);
    }

    return response.json();
};

const QboAttachmentsService = {
    getAttachments,
    getDownloadUrl,
    uploadAttachment,
    deleteAttachment,
    updateAttachment,
};

export default QboAttachmentsService;
